import Category from 'types/category';
import type { Transaction } from 'types/transaction';
import { categoryAncestorNamesPath } from './categorySearchRank';
import { getDirectChildCategories } from './categoryAccountNumberReorder';

/**
 * Shared chart-of-accounts rows (Cash, Sales, Product Sales, …).
 * Company-owned locked rows — including POS product leaves — are not shared.
 */
export function isSharedSystemCategory(category: {
  locked?: boolean;
  company?: { id: string } | null;
}): boolean {
  return category.locked === true && !category.company;
}

/** Resolve the direct parent category, if any. */
export function getParentCategory(
  category: Category,
  categories: { [key: string]: Category },
): Category | undefined {
  if (!category.parent) {
    return undefined;
  }
  const parentId =
    typeof category.parent === 'string' ? category.parent : category.parent.id;
  return categories[parentId];
}

// Helper function to get parent category name
export const getParentName = (
  category: Category,
  categories: { [key: string]: Category },
): string => {
  return getParentCategory(category, categories)?.name ?? '';
};

/** Parent line in pickers: `1000 Cash and Cash Equivalents` when account numbers are enabled. */
export function formatParentDisplayName(
  category: Category,
  categories: { [key: string]: Category },
  options?: FormatCategoryDisplayNameOptions,
): string {
  const parent = getParentCategory(category, categories);
  if (!parent) {
    return '';
  }
  return formatCategoryDisplayName(parent, options);
}

// Helper function to get root category name
export const getRootCategoryName = (
  category: Category,
  categories: { [key: string]: Category },
): string => {
  // If rootCategory is null, this is a root category itself
  if (!category.rootCategory) {
    return category.name;
  }
  // If rootCategory is already a Category object, use it directly
  if (typeof category.rootCategory === 'object') {
    return category.rootCategory.name;
  }
  // Otherwise, rootCategory is a string ID, look it up
  const rootCategory = categories[category.rootCategory];
  return rootCategory?.name || '';
};

/** Locked Current Assets category id under Assets, when present in the loaded chart. */
export function findCurrentAssetsCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const assetsRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'assets',
  );
  if (!assetsRoot) {
    return null;
  }

  const currentAssets = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === assetsRoot.id &&
      category.name.trim().toLowerCase() === 'current assets'
    );
  });

  return currentAssets?.id ?? null;
}

/** Cash and Cash Equivalents group under Current Assets (fallback: Current Assets). */
export function findCashAndCashEquivalentsCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const currentAssetsId = findCurrentAssetsCategoryId(categoriesById);
  if (!currentAssetsId) {
    return null;
  }
  const cashAndEquiv = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentAssetsId &&
      category.name.trim().toLowerCase() === 'cash and cash equivalents'
    );
  });
  return cashAndEquiv?.id ?? currentAssetsId;
}

/**
 * Locked system "Cash" under Cash and Cash Equivalents (or Current Assets on
 * older charts). Used as the CRJ/CDJ anchor — not selectable as a parent.
 */
export function findLockedCashCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const currentAssetsId = findCurrentAssetsCategoryId(categoriesById);
  if (!currentAssetsId) {
    return null;
  }

  const cashAndEquiv = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentAssetsId &&
      category.name.trim().toLowerCase() === 'cash and cash equivalents'
    );
  });

  if (cashAndEquiv) {
    const cashUnderCce = Object.values(categoriesById).find((category) => {
      if (category.isVoided) {
        return false;
      }
      const parentId =
        typeof category.parent === 'string'
          ? category.parent
          : (category.parent?.id ?? '');
      return (
        parentId === cashAndEquiv.id &&
        category.name.trim().toLowerCase() === 'cash'
      );
    });
    if (cashUnderCce) {
      return cashUnderCce.id;
    }
  }

  const cashUnderCurrentAssets = Object.values(categoriesById).find(
    (category) => {
      if (category.isVoided) {
        return false;
      }
      const parentId =
        typeof category.parent === 'string'
          ? category.parent
          : (category.parent?.id ?? '');
      return (
        parentId === currentAssetsId &&
        category.name.trim().toLowerCase() === 'cash'
      );
    },
  );

  return cashUnderCurrentAssets?.id ?? null;
}

/** True when parentId is the locked system Cash category. */
export function isLockedCashParentBlocked(
  parentId: string | null | undefined,
  categoriesById: Record<string, Category>,
): boolean {
  if (!parentId) {
    return false;
  }
  const lockedCashId = findLockedCashCategoryId(categoriesById);
  return lockedCashId !== null && parentId === lockedCashId;
}

/** Whether a category may be chosen as parent in category management pickers. */
export function isSelectableCategoryParent(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  return !isLockedCashParentBlocked(category.id, categoriesById);
}

/** True when a parent search query targets the locked Cash category name. */
export function searchQueryTargetsLockedCashParent(
  query: string,
  categoriesById: Record<string, Category>,
): boolean {
  const lockedCashId = findLockedCashCategoryId(categoriesById);
  if (!lockedCashId) {
    return false;
  }
  const lockedCash = Object.values(categoriesById).find(
    (category) => category.id === lockedCashId,
  );
  if (!lockedCash) {
    return false;
  }
  const normalizedQuery = query.replace(/\s+/g, ' ').trim().toLowerCase();
  if (!normalizedQuery) {
    return false;
  }
  if (normalizedQuery.includes(' and ') || normalizedQuery.includes('equiv')) {
    return false;
  }
  const lockedName = lockedCash.name.trim().toLowerCase();
  if (normalizedQuery === lockedName) {
    return true;
  }
  if (lockedName === 'cash') {
    return (
      normalizedQuery === 'cash' ||
      (normalizedQuery.startsWith('cash') && normalizedQuery.length <= 5)
    );
  }
  return lockedName.includes(normalizedQuery) && normalizedQuery.length >= 2;
}

/** True when the category is Current Assets or a descendant of it. */
export function isCategoryUnderCurrentAssets(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  const currentAssetsId = findCurrentAssetsCategoryId(categoriesById);
  if (!currentAssetsId) {
    return false;
  }
  if (category.id === currentAssetsId) {
    return true;
  }
  return isCategoryDescendantOf(category, currentAssetsId, categoriesById);
}

/**
 * Treat as cash applies to new/edited categories whose parent is Current Assets
 * or a descendant (e.g. Cash and Cash Equivalents, Cash in Bank).
 */
export function isCountsAsCashParentEligible(
  parentId: string | null | undefined,
  categoriesById: Record<string, Category>,
): boolean {
  if (!parentId) {
    return false;
  }
  const parent = categoriesById[parentId];
  if (!parent || parent.isVoided) {
    return false;
  }
  return isCategoryUnderCurrentAssets(parent, categoriesById);
}

/** Leaf account flagged Treat as cash (Cash, bank, e-wallet, cash equivalents). */
export function isCashEquivalentLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided || category.countsAsCash !== true) {
    return false;
  }
  return !categoryHasActiveTransactionChild(category.id, categoriesById);
}

/** Locked system "Withholding Taxes Payable" under Current Liabilities. */
export function findLockedWithholdingTaxPayableCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const liabilitiesRootId = findLiabilitiesRootCategoryId(categoriesById);
  if (!liabilitiesRootId) {
    return null;
  }

  const currentLiabilities = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === liabilitiesRootId &&
      category.name.trim().toLowerCase() === 'current liabilities'
    );
  });
  if (!currentLiabilities) {
    return null;
  }

  const matches = Object.values(categoriesById).filter((category) => {
    if (category.isVoided || category.locked === false) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentLiabilities.id &&
      category.name.trim().toLowerCase() === 'withholding taxes payable' &&
      !categoryHasActiveTransactionChild(category.id, categoriesById)
    );
  });
  const preferred =
    matches.find((category) => category.locked === true) ?? matches[0];
  return preferred?.id ?? null;
}

/** The locked liability account used for EWT withheld from supplier payments. */
export function isWithholdingTaxPayableCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const lockedId = findLockedWithholdingTaxPayableCategoryId(categoriesById);
  if (lockedId) {
    return category.id === lockedId;
  }
  // Fallback for charts that omit `locked` in the client payload: require the
  // locked-system name, leaf status, and Current Liabilities parent path.
  if (category.name.trim().toLowerCase() !== 'withholding taxes payable') {
    return false;
  }
  if (categoryHasActiveTransactionChild(category.id, categoriesById)) {
    return false;
  }
  const liabilitiesRootId = findLiabilitiesRootCategoryId(categoriesById);
  if (!liabilitiesRootId) {
    return false;
  }
  const parentId =
    typeof category.parent === 'string'
      ? category.parent
      : (category.parent?.id ?? '');
  const parent = categoriesById[parentId];
  return (
    !!parent &&
    !parent.isVoided &&
    parent.name.trim().toLowerCase() === 'current liabilities' &&
    (typeof parent.parent === 'string'
      ? parent.parent
      : (parent.parent?.id ?? '')) === liabilitiesRootId
  );
}

/**
 * System locked Cash and any leaf flagged Treat as cash — used by custom-entry
 * redirect gates for cash expense / cash receipt patterns.
 */
export function isTreatedAsCashCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const lockedCashId = findLockedCashCategoryId(categoriesById);
  if (lockedCashId && category.id === lockedCashId) {
    return true;
  }
  return isCashEquivalentLeafCategory(category, categoriesById);
}

/** True when this row can be multi-selected (bulk delete or cash treatment). */
export const isCategoryBulkSelectable = (category: Category): boolean =>
  !!category.parent && !category.isVoided;

/** Keys for `data-root` on category rows; colors live in `categories/index.module.scss`. */
export type RootCategoryTintKey =
  'assets' | 'revenue' | 'expenses' | 'liabilities' | 'equity';

const ROOT_CATEGORY_TINT_KEYS = new Set<string>([
  'assets',
  'revenue',
  'expenses',
  'liabilities',
  'equity',
]);

export function getRootCategoryTintKey(
  rootCategoryName: string,
): RootCategoryTintKey | null {
  const key = rootCategoryName.trim().toLowerCase();
  return ROOT_CATEGORY_TINT_KEYS.has(key) ? (key as RootCategoryTintKey) : null;
}

/**
 * Sibling sort for the categories page: account number (natural) when set,
 * then name. Categories without a number sort after numbered siblings.
 */
export function compareCategoriesByAccountNumber(
  a: Category,
  b: Category,
): number {
  const aCode = a.accountNumber?.trim() ?? '';
  const bCode = b.accountNumber?.trim() ?? '';

  if (aCode && bCode) {
    const byCode = aCode.localeCompare(bCode, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    if (byCode !== 0) {
      return byCode;
    }
  } else if (aCode !== bCode) {
    return aCode ? -1 : 1;
  }

  return a.name.localeCompare(b.name);
}

export type FormatCategoryDisplayNameOptions = {
  /** When false, returns `category.name` only. Defaults to true. */
  includeAccountNumber?: boolean;
};

/** Normalize account codes for search (trim, drop spaces, lowercase). */
export function normalizeAccountNumberForSearch(
  value: string | number | null | undefined,
): string {
  if (value == null) return '';
  return String(value).trim().replace(/\s+/g, '').toLowerCase();
}

/** True when the query is only digits (e.g. `1`, `1100`). */
export function isNumericCategorySearchQuery(query: string): boolean {
  return /^\d+$/.test(query.trim());
}

/** Substring match on normalized account number. */
export function categoryAccountNumberMatchesQuery(
  query: string,
  category: Category,
): boolean {
  const q = normalizeAccountNumberForSearch(query);
  if (!q) return false;
  const code = normalizeAccountNumberForSearch(category.accountNumber);
  return code.length > 0 && code.includes(q);
}

/**
 * Category picker search: name, parent name, and (when enabled) account number.
 */
export function categoryMatchesPickerSearchQuery(
  query: string,
  category: Category,
  categoriesById: Record<string, Category>,
  options: { accountNumbersEnabled: boolean },
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const parent = getParentCategory(category, categoriesById);
  const nameMatch = category.name.toLowerCase().includes(q);
  const parentNameMatch = parent
    ? parent.name.toLowerCase().includes(q)
    : false;
  const parentAccountMatch =
    options.accountNumbersEnabled &&
    parent != null &&
    categoryAccountNumberMatchesQuery(query, parent);
  const accountMatch =
    options.accountNumbersEnabled &&
    categoryAccountNumberMatchesQuery(query, category);

  return nameMatch || parentNameMatch || parentAccountMatch || accountMatch;
}

/** List label: `1100 Cash` when an account number exists, otherwise `Cash`. */
export function formatCategoryDisplayName(
  category: Category,
  options?: FormatCategoryDisplayNameOptions,
): string {
  if (options?.includeAccountNumber === false) {
    return category.name;
  }
  const code = category.accountNumber?.trim();
  if (!code) {
    return category.name;
  }
  return `${code} ${category.name}`;
}

/** Overlay inline account-number drafts for list sort while editing. */
export function categoryWithAccountNumberDraft(
  category: Category,
  accountNumberDrafts: Record<string, string>,
): Category {
  if (!(category.id in accountNumberDrafts)) {
    return category;
  }
  const trimmed = accountNumberDrafts[category.id].trim();
  return {
    ...category,
    accountNumber: trimmed === '' ? undefined : trimmed,
  };
}

/** Same category map shape as the store, with draft account numbers applied. */
export function mergeCategoriesWithAccountNumberDrafts(
  categories: Record<string, Category>,
  accountNumberDrafts: Record<string, string>,
): Record<string, Category> {
  const merged: Record<string, Category> = {};
  for (const [id, category] of Object.entries(categories)) {
    merged[id] = categoryWithAccountNumberDraft(category, accountNumberDrafts);
  }
  return merged;
}

/**
 * Re-sorts flattened rows by account number without breaking the tree: each parent's
 * subtree stays contiguous; only sibling order changes (numbered first, unnumbered last).
 */

export function sortFlattenedRowsPreservingHierarchy<
  T extends { category: Category; depth: number },
>(rows: T[], accountNumberDrafts?: Record<string, string>): T[] {
  if (rows.length === 0) {
    return rows;
  }

  const compareRow = (a: T, b: T): number => {
    const catA = accountNumberDrafts
      ? categoryWithAccountNumberDraft(a.category, accountNumberDrafts)
      : a.category;
    const catB = accountNumberDrafts
      ? categoryWithAccountNumberDraft(b.category, accountNumberDrafts)
      : b.category;
    return compareCategoriesByAccountNumber(catA, catB);
  };

  const result: T[] = [];
  let index = 0;

  const appendSubtree = (depth: number): void => {
    const siblings: T[] = [];
    while (index < rows.length && rows[index].depth === depth) {
      siblings.push(rows[index]);
      index += 1;
    }
    siblings.sort(compareRow);
    for (const row of siblings) {
      result.push(row);
      appendSubtree(depth + 1);
    }
  };

  while (index < rows.length) {
    appendSubtree(rows[index].depth);
  }

  return result;
}

// Flatten categories and sort by hierarchy (children immediately after parents)
export const flattenCategories = (categories: {
  [key: string]: Category;
}): Array<{
  category: Category;
  depth: number;
  parentName: string;
  rootCategoryName: string;
}> => {
  const result: Array<{
    category: Category;
    depth: number;
    parentName: string;
    rootCategoryName: string;
  }> = [];
  const processed = new Set<string>();

  // Recursively add a category and all its children
  const addCategoryAndChildren = (category: Category, depth: number) => {
    if (processed.has(category.id)) {
      return;
    }

    const parentName = getParentName(category, categories);
    const rootCategoryName = getRootCategoryName(category, categories);
    result.push({
      category,
      depth,
      parentName,
      rootCategoryName,
    });
    processed.add(category.id);

    const sortedChildren = getDirectChildCategories(
      categories,
      category.id,
    ).sort(compareCategoriesByAccountNumber);

    sortedChildren.forEach((child) => {
      addCategoryAndChildren(child, depth + 1);
    });
  };

  const rootCategories = getDirectChildCategories(categories, null).sort(
    compareCategoriesByAccountNumber,
  );
  rootCategories.forEach((rootCategory) => {
    addCategoryAndChildren(rootCategory, 0);
  });

  return result;
};

/**
 * Check if a category is a root category (has no parent)
 */
export const isRootCategory = (category: Category): boolean => {
  return !category.parent || category.parent === null;
};

/**
 * Result of checking whether an account title conflicts with the company chart.
 */
export type CategoryNameConflict =
  | { kind: 'active'; message: string }
  | { kind: 'voided'; message: string; voidedCategoryId: string };

function buildActiveDuplicateMessage(
  name: string,
  rootCategoryName: string,
): string {
  return `Account title '${name.trim()}' already exists under ${rootCategoryName}.`;
}

function buildVoidedDuplicateMessage(
  name: string,
  rootCategoryName: string,
): string {
  return `Account title '${name.trim()}' was previously voided under ${rootCategoryName}. Restore that category instead of creating a new one.`;
}

/**
 * Check if a category name conflicts with active or voided accounts company-wide.
 */
export function findCategoryNameConflict(
  activeCategories: { [key: string]: Category },
  name: string,
  voidedCategories: { [key: string]: Category } = {},
  excludeCategoryId?: string,
): CategoryNameConflict | null {
  const trimmedName = name.trim().toLowerCase();
  const lookupCategories = { ...activeCategories, ...voidedCategories };

  for (const categoryId in activeCategories) {
    if (excludeCategoryId && categoryId === excludeCategoryId) {
      continue;
    }

    const category = activeCategories[categoryId];

    if (category.isVoided) {
      continue;
    }

    if (category.name.trim().toLowerCase() === trimmedName) {
      const rootCategoryName =
        getRootCategoryName(category, lookupCategories) || 'chart of accounts';
      return {
        kind: 'active',
        message: buildActiveDuplicateMessage(name, rootCategoryName),
      };
    }
  }

  for (const categoryId in voidedCategories) {
    if (excludeCategoryId && categoryId === excludeCategoryId) {
      continue;
    }

    const category = voidedCategories[categoryId];

    if (category.name.trim().toLowerCase() === trimmedName) {
      const rootCategoryName =
        getRootCategoryName(category, lookupCategories) || 'chart of accounts';
      return {
        kind: 'voided',
        voidedCategoryId: categoryId,
        message: buildVoidedDuplicateMessage(name, rootCategoryName),
      };
    }
  }

  return null;
}

/**
 * Check if a category name already exists anywhere in the company chart of accounts.
 * @param categories - Active categories object
 * @param name - Category name to check
 * @param _parentId - Parent category ID (unused; kept for call-site compatibility)
 * @param excludeCategoryId - Optional category ID to exclude from check (for edit operations)
 * @param voidedCategories - Voided categories reserved for the same account titles
 * @returns Error message if duplicate found, null otherwise
 */
export const validateDuplicateCategoryName = (
  categories: { [key: string]: Category },
  name: string,
  _parentId: string,
  excludeCategoryId?: string,
  voidedCategories: { [key: string]: Category } = {},
): string | null => {
  return (
    findCategoryNameConflict(
      categories,
      name,
      voidedCategories,
      excludeCategoryId,
    )?.message ?? null
  );
};

/**
 * Whether the company chart has any non-voided child under this category.
 */
export function categoryHasActiveTransactionChild(
  categoryId: string,
  categoriesById: Record<string, Category>,
): boolean {
  for (const cat of Object.values(categoriesById)) {
    if (cat.isVoided) continue;
    const pid =
      typeof cat.parent === 'string' ? cat.parent : (cat.parent?.id ?? '');
    if (pid === categoryId) return true;
  }
  return false;
}

/**
 * Entries must use leaf accounts: not a chart root, and no active subcategories.
 */
export function validateCategoryForTransaction(
  category: Category,
  categoriesById: Record<string, Category>,
): string | null {
  if (isRootCategory(category)) {
    return `Cannot use root category "${category.name}" directly in transaction entries. Please create a subcategory under it and use that instead.`;
  }
  if (categoryHasActiveTransactionChild(category.id, categoriesById)) {
    return `Cannot use "${category.name}" in transaction entries because it has subcategories. Choose a leaf account instead.`;
  }
  return null;
}

/** Root → leaf breadcrumb for banner copy (e.g. Assets → Cash → Gcash). */
export function formatCategoryBreadcrumbPath(
  category: Category,
  categoriesById: Record<string, Category>,
): string {
  const ancestors = categoryAncestorNamesPath(category, categoriesById);
  return [...ancestors, category.name].join(' → ');
}

export type NonLeafTransactionCategoryScanOptions = {
  /** When false, voided transactions and voided lines are ignored (default). */
  includeVoided?: boolean;
};

/**
 * True when any loaded transaction line uses a non-leaf chart account
 * (root or a category that still has active subcategories).
 */
export function transactionsHaveNonLeafCategoryEntries(
  transactions: Transaction[],
  categoriesById: Record<string, Category>,
  options?: NonLeafTransactionCategoryScanOptions,
): boolean {
  return (
    findFirstNonLeafTransactionCategory(
      transactions,
      categoriesById,
      options,
    ) != null
  );
}

export type NonLeafTransactionCategoryIssueSummary = {
  /** Distinct transactions with at least one non-leaf line. */
  transactionCount: number;
  /** Total journal lines using a root or parent-with-children account. */
  lineCount: number;
  /** Distinct offending category ids (for quick review). */
  categoryIds: string[];
};

function isNonLeafTransactionLine(
  entry: { category: { id: string }; isVoided?: boolean },
  categoriesById: Record<string, Category>,
  includeVoided: boolean,
): boolean {
  if (!includeVoided && entry.isVoided) {
    return false;
  }
  const category = categoriesById[entry.category.id];
  if (!category || category.isVoided) {
    return false;
  }
  return validateCategoryForTransaction(category, categoriesById) !== null;
}

/** Count transactions/lines using root or parent accounts (for banner copy). */
export function summarizeNonLeafTransactionCategoryIssues(
  transactions: Transaction[],
  categoriesById: Record<string, Category>,
  options?: NonLeafTransactionCategoryScanOptions,
): NonLeafTransactionCategoryIssueSummary {
  const includeVoided = options?.includeVoided === true;
  let transactionCount = 0;
  let lineCount = 0;
  const categoryIds = new Set<string>();

  for (const transaction of transactions) {
    if (!includeVoided && transaction.isVoided) {
      continue;
    }
    let transactionHasIssue = false;
    for (const entry of transaction.entries) {
      if (!isNonLeafTransactionLine(entry, categoriesById, includeVoided)) {
        continue;
      }
      transactionHasIssue = true;
      lineCount += 1;
      categoryIds.add(entry.category.id);
    }
    if (transactionHasIssue) {
      transactionCount += 1;
    }
  }

  return {
    transactionCount,
    lineCount,
    categoryIds: [...categoryIds],
  };
}

export type NonLeafCategoryIssueDetail = {
  categoryId: string;
  categoryName: string;
  hierarchyPath: string;
  issueLabel: string;
  lineCount: number;
  transactionCount: number;
  transactions: Array<{
    id: string;
    description: string;
    date: string;
  }>;
};

function nonLeafCategoryIssueLabel(
  category: Category,
  categoriesById: Record<string, Category>,
): string {
  if (isRootCategory(category)) {
    return 'Top-level chart category';
  }
  if (categoryHasActiveTransactionChild(category.id, categoriesById)) {
    return 'Group account (has subcategories)';
  }
  return 'Not a leaf account';
}

/** Per group-account breakdown for the transactions page banner modal. */
export function listNonLeafTransactionCategoryIssueDetails(
  transactions: Transaction[],
  categoriesById: Record<string, Category>,
  options?: NonLeafTransactionCategoryScanOptions,
): NonLeafCategoryIssueDetail[] {
  const includeVoided = options?.includeVoided === true;
  const byCategory = new Map<
    string,
    {
      lineCount: number;
      transactionIds: Set<string>;
      transactions: NonLeafCategoryIssueDetail['transactions'];
    }
  >();

  for (const transaction of transactions) {
    if (!includeVoided && transaction.isVoided) {
      continue;
    }
    for (const entry of transaction.entries) {
      if (!isNonLeafTransactionLine(entry, categoriesById, includeVoided)) {
        continue;
      }
      const categoryId = entry.category.id;
      let bucket = byCategory.get(categoryId);
      if (!bucket) {
        bucket = {
          lineCount: 0,
          transactionIds: new Set<string>(),
          transactions: [],
        };
        byCategory.set(categoryId, bucket);
      }
      bucket.lineCount += 1;
      if (!bucket.transactionIds.has(transaction.id)) {
        bucket.transactionIds.add(transaction.id);
        bucket.transactions.push({
          id: transaction.id,
          description:
            (transaction.description ?? '').trim() || '(no description)',
          date: transaction.date,
        });
      }
    }
  }

  const details: NonLeafCategoryIssueDetail[] = [];
  for (const [categoryId, bucket] of byCategory.entries()) {
    const category = categoriesById[categoryId];
    if (!category) continue;
    details.push({
      categoryId,
      categoryName: category.name.trim(),
      hierarchyPath: formatCategoryBreadcrumbPath(category, categoriesById),
      issueLabel: nonLeafCategoryIssueLabel(category, categoriesById),
      lineCount: bucket.lineCount,
      transactionCount: bucket.transactionIds.size,
      transactions: bucket.transactions.sort((a, b) =>
        b.date.localeCompare(a.date),
      ),
    });
  }

  details.sort((a, b) => {
    if (b.lineCount !== a.lineCount) {
      return b.lineCount - a.lineCount;
    }
    return a.categoryName.localeCompare(b.categoryName);
  });

  return details;
}

/** First non-leaf category referenced on a transaction line, for banner examples. */
export function findFirstNonLeafTransactionCategory(
  transactions: Transaction[],
  categoriesById: Record<string, Category>,
  options?: NonLeafTransactionCategoryScanOptions,
): Category | null {
  const includeVoided = options?.includeVoided === true;

  for (const transaction of transactions) {
    if (!includeVoided && transaction.isVoided) {
      continue;
    }
    for (const entry of transaction.entries) {
      if (isNonLeafTransactionLine(entry, categoriesById, includeVoided)) {
        return categoriesById[entry.category.id] ?? null;
      }
    }
  }
  return null;
}

/** Locked Revenue > Sales category id when present in the loaded chart. */
export function findSalesCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const revenueRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'revenue',
  );
  if (!revenueRoot) {
    return null;
  }

  const salesCategory = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return parentId === revenueRoot.id && category.name === 'Sales';
  });

  return salesCategory?.id ?? null;
}

function isCategoryDescendantOf(
  category: Category,
  ancestorId: string,
  categoriesById: Record<string, Category>,
): boolean {
  let current: Category | undefined = category;
  const visited = new Set<string>();

  while (current) {
    if (current.id === ancestorId) {
      return true;
    }
    if (visited.has(current.id)) {
      break;
    }
    visited.add(current.id);

    const parentId: string =
      typeof current.parent === 'string'
        ? current.parent
        : (current.parent?.id ?? '');
    if (!parentId) {
      return false;
    }
    current = categoriesById[parentId];
  }

  return false;
}

/** Sales itself or any account in the Revenue > Sales subtree (for new-category parent picker). */
export function isSalesCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const salesCategoryId = findSalesCategoryId(categoriesById);
  if (!salesCategoryId) {
    return false;
  }
  if (category.id === salesCategoryId) {
    return true;
  }
  return isCategoryDescendantOf(category, salesCategoryId, categoriesById);
}

/** Leaf accounts under Revenue > Sales (for cash receipts journal revenue line). */
export function isSalesLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (validateCategoryForTransaction(category, categoriesById)) {
    return false;
  }
  const salesCategoryId = findSalesCategoryId(categoriesById);
  if (!salesCategoryId) {
    return false;
  }
  return isCategoryDescendantOf(category, salesCategoryId, categoriesById);
}

/**
 * Auto-created per-product sales leaves look like
 * `Coffee Sales (550e8400-e29b-41d4-a716-446655440000)`.
 */
export function isAutoCreatedProductSalesCategory(category: Category): boolean {
  return /^.+\sSales\s\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)$/i.test(
    category.name.trim(),
  );
}

/**
 * POS product accounting picker: Revenue > Sales subtree options
 * (Product Sales, Service Sales, and manually created accounts). Excludes
 * auto-created per-product sales leaves and the Sales group itself.
 */
export function isProductFormAccountingCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  if (isAutoCreatedProductSalesCategory(category)) {
    return false;
  }
  const salesCategoryId = findSalesCategoryId(categoriesById);
  if (!salesCategoryId || category.id === salesCategoryId) {
    return false;
  }
  return isCategoryDescendantOf(category, salesCategoryId, categoriesById);
}

function findDirectChildCategoryId(
  categoriesById: Record<string, Category>,
  parentId: string,
  childName: string,
): string | null {
  const needle = childName.trim().toLowerCase();
  const match = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parent =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return parent === parentId && category.name.trim().toLowerCase() === needle;
  });
  return match?.id ?? null;
}

/** Locked Revenue > Sales > Product Sales. */
export function findProductSalesCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const salesCategoryId = findSalesCategoryId(categoriesById);
  if (!salesCategoryId) {
    return null;
  }
  return findDirectChildCategoryId(
    categoriesById,
    salesCategoryId,
    'Product Sales',
  );
}

/** Locked Revenue > Sales > Service Sales. */
export function findServiceSalesCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const salesCategoryId = findSalesCategoryId(categoriesById);
  if (!salesCategoryId) {
    return null;
  }
  return findDirectChildCategoryId(
    categoriesById,
    salesCategoryId,
    'Service Sales',
  );
}

/** Locked Expenses > Cost of Goods Sold. */
export function findCostOfGoodsSoldCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const expensesRootId = findExpensesRootCategoryId(categoriesById);
  if (!expensesRootId) {
    return null;
  }
  return findDirectChildCategoryId(
    categoriesById,
    expensesRootId,
    'Cost of Goods Sold',
  );
}

/** Locked Expenses > Cost of Services (also accepts Cost of Service). */
export function findCostOfServicesCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const expensesRootId = findExpensesRootCategoryId(categoriesById);
  if (!expensesRootId) {
    return null;
  }
  return (
    findDirectChildCategoryId(
      categoriesById,
      expensesRootId,
      'Cost of Services',
    ) ??
    findDirectChildCategoryId(categoriesById, expensesRootId, 'Cost of Service')
  );
}

/**
 * Expense anchor for the product form: Cost of Goods Sold when stock is
 * tracked, otherwise Cost of Services.
 */
export function productFormExpenseAnchorCategoryId(
  categoriesById: Record<string, Category>,
  trackInventory: boolean,
): string | null {
  return trackInventory
    ? findCostOfGoodsSoldCategoryId(categoriesById)
    : findCostOfServicesCategoryId(categoriesById);
}

/** Sales anchor for the product form: Product Sales when stock is tracked, otherwise Service Sales. */
export function productFormSalesAnchorCategoryId(
  categoriesById: Record<string, Category>,
  trackInventory: boolean,
): string | null {
  return trackInventory
    ? findProductSalesCategoryId(categoriesById)
    : findServiceSalesCategoryId(categoriesById);
}

function productFormAnchorHasManagedDescendant(
  anchorId: string,
  categoriesById: Record<string, Category>,
): boolean {
  return Object.values(categoriesById).some((category) => {
    if (category.isVoided || category.id === anchorId) {
      return false;
    }
    if (isAutoCreatedProductSalesCategory(category)) {
      return false;
    }
    return isCategoryDescendantOf(category, anchorId, categoriesById);
  });
}

function productFormAnchorCategoryHasManagedChild(
  categoryId: string,
  categoriesById: Record<string, Category>,
): boolean {
  return Object.values(categoriesById).some((category) => {
    if (category.isVoided || isAutoCreatedProductSalesCategory(category)) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return parentId === categoryId;
  });
}

/**
 * Accounts a product can post to under an anchor (Product Sales, Service Sales,
 * Cost of Goods Sold, or Cost of Services).
 *
 * When nothing has been added under the anchor, only the anchor is selectable.
 * When accounts exist under it, those leaf accounts are selectable and the
 * anchor itself is not.
 */
export function isProductFormAnchorCategoryOption(
  category: Category,
  categoriesById: Record<string, Category>,
  anchorId: string | null,
): boolean {
  if (!anchorId || category.isVoided) {
    return false;
  }
  if (isAutoCreatedProductSalesCategory(category)) {
    return false;
  }
  if (!isCategoryDescendantOf(category, anchorId, categoriesById)) {
    return false;
  }

  const hasManagedDescendants = productFormAnchorHasManagedDescendant(
    anchorId,
    categoriesById,
  );
  if (!hasManagedDescendants) {
    return category.id === anchorId;
  }
  if (category.id === anchorId) {
    return false;
  }
  return !productFormAnchorCategoryHasManagedChild(category.id, categoriesById);
}

/**
 * When the anchor has no accounts under it, that anchor is the only choice and
 * can be pre-selected. Otherwise the user must pick a leaf.
 */
export function soleSelectableProductFormAnchorCategoryId(
  categoriesById: Record<string, Category>,
  anchorId: string | null,
): string {
  if (!anchorId) {
    return '';
  }
  const anchor = categoriesById[anchorId];
  if (!anchor || anchor.isVoided) {
    return '';
  }
  if (productFormAnchorHasManagedDescendant(anchorId, categoriesById)) {
    return '';
  }
  return anchorId;
}

/** Anchor or any account under it, for creating a new category beneath that anchor. */
export function isProductFormAnchorParentCategory(
  category: Category,
  categoriesById: Record<string, Category>,
  anchorId: string | null,
): boolean {
  if (!anchorId || category.isVoided) {
    return false;
  }
  if (isAutoCreatedProductSalesCategory(category)) {
    return false;
  }
  return isCategoryDescendantOf(category, anchorId, categoriesById);
}

/** Locked Inventory group under Assets > Current Assets. */
export function findInventoryCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const currentAssetsId = findCurrentAssetsCategoryId(categoriesById);
  if (!currentAssetsId) {
    return null;
  }

  const inventory = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentAssetsId &&
      category.name.trim().toLowerCase() === 'inventory'
    );
  });

  return inventory?.id ?? null;
}

/** Inventory itself or any descendant (item types / inventory asset accounts). */
export function isInventoryCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const inventoryId = findInventoryCategoryId(categoriesById);
  if (!inventoryId) {
    return false;
  }
  if (category.id === inventoryId) {
    return true;
  }
  return isCategoryDescendantOf(category, inventoryId, categoriesById);
}

/**
 * Selectable inventory item types: accounts under Inventory (not the Inventory
 * group itself). Includes Finished Goods, Merchandise, Raw Materials, WIP, etc.
 */
export function isInventoryItemTypeCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const inventoryId = findInventoryCategoryId(categoriesById);
  if (!inventoryId || category.id === inventoryId) {
    return false;
  }
  if (!isCategoryDescendantOf(category, inventoryId, categoriesById)) {
    return false;
  }
  // Only leaf accounts — parent inventory groups cannot be assigned to products.
  return !categoryHasActiveTransactionChild(category.id, categoriesById);
}

/** Locked Assets root category id when present in the loaded chart. */
export function findAssetsRootCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const assetsRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'assets',
  );
  return assetsRoot?.id ?? null;
}

/** Assets root or any account in the Assets subtree. */
export function isAssetCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const assetsRootId = findAssetsRootCategoryId(categoriesById);
  if (!assetsRootId) {
    return false;
  }
  if (category.id === assetsRootId) {
    return true;
  }
  return isCategoryDescendantOf(category, assetsRootId, categoriesById);
}

/** Locked Expenses root category id when present in the loaded chart. */
export function findExpensesRootCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const expensesRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'expenses',
  );
  return expensesRoot?.id ?? null;
}

/** Expenses root or any account in the Expenses subtree (for new-category parent picker). */
export function isExpenseCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const expensesRootId = findExpensesRootCategoryId(categoriesById);
  if (!expensesRootId) {
    return false;
  }
  if (category.id === expensesRootId) {
    return true;
  }
  return isCategoryDescendantOf(category, expensesRootId, categoriesById);
}

/** Leaf accounts under Expenses (for cash disbursements journal expense line). */
export function isExpenseLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (validateCategoryForTransaction(category, categoriesById)) {
    return false;
  }
  const expensesRootId = findExpensesRootCategoryId(categoriesById);
  if (!expensesRootId) {
    return false;
  }
  return isCategoryDescendantOf(category, expensesRootId, categoriesById);
}

/** Locked "Accounts Receivable" group under Assets > Current Assets. */
export function findAccountsReceivableCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const currentAssetsId = findCurrentAssetsCategoryId(categoriesById);
  if (!currentAssetsId) return null;

  const ar = Object.values(categoriesById).find((category) => {
    if (category.isVoided) return false;
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentAssetsId &&
      category.name.trim().toLowerCase() === 'accounts receivable'
    );
  });
  return ar?.id ?? null;
}

/**
 * Leaf under Accounts Receivable (including Accounts Receivable itself when it
 * is a leaf). Used for cash receipts journal credit lines (collections).
 */
export function isAccountsReceivableLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (validateCategoryForTransaction(category, categoriesById)) {
    return false;
  }
  const arId = findAccountsReceivableCategoryId(categoriesById);
  if (!arId) {
    return false;
  }
  return isCategoryDescendantOf(category, arId, categoriesById);
}

/** Accounts Receivable group or any descendant (for category create parents). */
export function isAccountsReceivableCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const arId = findAccountsReceivableCategoryId(categoriesById);
  if (!arId) {
    return false;
  }
  return isCategoryDescendantOf(category, arId, categoriesById);
}

/**
 * Cash receipts journal preferred credit-side accounts (empty-search browse):
 * Sales leaves or Accounts Receivable leaves. Search may still offer other
 * postable leaves via the journal leaf filter.
 */
export function isCashReceiptCreditLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  return (
    isSalesLeafCategory(category, categoriesById) ||
    isAccountsReceivableLeafCategory(category, categoriesById)
  );
}

/** Locked "Accounts Payable" group under Liabilities > Current Liabilities. */
export function findAccountsPayableCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const liabilitiesRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'liabilities',
  );
  if (!liabilitiesRoot) return null;

  const currentLiabilities = Object.values(categoriesById).find((category) => {
    if (category.isVoided) return false;
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === liabilitiesRoot.id &&
      category.name.trim().toLowerCase() === 'current liabilities'
    );
  });
  if (!currentLiabilities) return null;

  const ap = Object.values(categoriesById).find((category) => {
    if (category.isVoided) return false;
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === currentLiabilities.id &&
      category.name.trim().toLowerCase() === 'accounts payable'
    );
  });
  return ap?.id ?? null;
}

/** Trade Accounts Payable when present; otherwise the Accounts Payable group id. */
export function findResolvedAccountsPayableCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const apParentId = findAccountsPayableCategoryId(categoriesById);
  if (!apParentId) {
    return null;
  }

  const tradeAp = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId =
      typeof category.parent === 'string'
        ? category.parent
        : (category.parent?.id ?? '');
    return (
      parentId === apParentId &&
      category.name.trim().toLowerCase() === 'trade accounts payable'
    );
  });

  return tradeAp?.id ?? apParentId;
}

function categoryParentIdForLookup(category: Category): string {
  if (typeof category.parent === 'string') {
    return category.parent;
  }
  return category.parent?.id ?? '';
}

export function findEquityRootCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const equity = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    const parentId = categoryParentIdForLookup(category);
    return !parentId && category.name.trim().toLowerCase() === 'equity';
  });
  return equity?.id ?? null;
}

function findEquityLeafCategoryIdByName(
  categoriesById: Record<string, Category>,
  equityRootId: string,
  targetName: string,
): string | null {
  const match = Object.values(categoriesById).find((category) => {
    if (category.isVoided) {
      return false;
    }
    return (
      categoryParentIdForLookup(category) === equityRootId &&
      category.name.trim().toLowerCase() === targetName.toLowerCase() &&
      !categoryHasActiveTransactionChild(category.id, categoriesById)
    );
  });
  return match?.id ?? null;
}

export function findInvestedCapitalCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const equityRootId = findEquityRootCategoryId(categoriesById);
  if (!equityRootId) {
    return null;
  }
  return findEquityLeafCategoryIdByName(
    categoriesById,
    equityRootId,
    'Invested Capital',
  );
}

export function findWithdrawalEquityCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const equityRootId = findEquityRootCategoryId(categoriesById);
  if (!equityRootId) {
    return null;
  }
  return (
    findEquityLeafCategoryIdByName(
      categoriesById,
      equityRootId,
      'Owner Distribution',
    ) ??
    findEquityLeafCategoryIdByName(categoriesById, equityRootId, 'Dividends')
  );
}

/** All non-voided descendant category IDs (inclusive of the given root). */
export function collectDescendantCategoryIds(
  rootId: string,
  categoriesById: Record<string, Category>,
): Set<string> {
  const ids = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const currentId = queue.pop()!;
    const cat = categoriesById[currentId];
    if (!cat || cat.isVoided || ids.has(currentId)) continue;
    ids.add(currentId);
    for (const childId of cat.children ?? []) {
      queue.push(childId);
    }
  }
  return ids;
}

const CATEGORY_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** AI / import placeholder ids (e.g. `local_expense_1`). */
export function isLocalCategoryPlaceholderId(
  categoryId: string | undefined | null,
): boolean {
  return /^local_/i.test((categoryId ?? '').trim());
}

/** Draft line that still needs a real category before posting. */
export function isDraftCategoryPlaceholderEntry(entry: {
  categoryId: string;
  parentId?: string;
  name?: string;
}): boolean {
  const name = entry.name?.trim();
  const parentId = entry.parentId?.trim();
  if (!name || !parentId) {
    return false;
  }
  const id = entry.categoryId.trim();
  if (isLocalCategoryPlaceholderId(id)) {
    return true;
  }
  return !CATEGORY_UUID_REGEX.test(id);
}

/** Leaf accounts under Assets (cash, inventory, equipment, etc.). */
export function isAssetLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (validateCategoryForTransaction(category, categoriesById)) {
    return false;
  }
  const assetsRootId = findAssetsRootCategoryId(categoriesById);
  if (!assetsRootId) {
    return false;
  }
  return isCategoryDescendantOf(category, assetsRootId, categoriesById);
}

/**
 * AP bill Bill For lines: leaf under Expenses or Assets (expenses, cash,
 * inventory, non-current assets, etc.).
 */
export function isApBillDebitLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  return (
    isExpenseLeafCategory(category, categoriesById) ||
    isAssetLeafCategory(category, categoriesById)
  );
}

/** Expenses, Assets, or descendants (for Bill For category create parent picker). */
export function isApBillDebitCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  return (
    isExpenseCategoryOrDescendant(category, categoriesById) ||
    isAssetCategoryOrDescendant(category, categoriesById)
  );
}

/** Locked Liabilities root category id when present in the loaded chart. */
export function findLiabilitiesRootCategoryId(
  categoriesById: Record<string, Category>,
): string | null {
  const liabilitiesRoot = Object.values(categoriesById).find(
    (category) =>
      isRootCategory(category) &&
      !category.isVoided &&
      category.name.trim().toLowerCase() === 'liabilities',
  );
  return liabilitiesRoot?.id ?? null;
}

/** Liabilities root or any account in the Liabilities subtree. */
export function isLiabilityCategoryOrDescendant(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (category.isVoided) {
    return false;
  }
  const liabilitiesRootId = findLiabilitiesRootCategoryId(categoriesById);
  if (!liabilitiesRootId) {
    return false;
  }
  if (category.id === liabilitiesRootId) {
    return true;
  }
  return isCategoryDescendantOf(category, liabilitiesRootId, categoriesById);
}

/** Leaf accounts under Liabilities (for AP bill credit lines). */
export function isLiabilityLeafCategory(
  category: Category,
  categoriesById: Record<string, Category>,
): boolean {
  if (validateCategoryForTransaction(category, categoriesById)) {
    return false;
  }
  const liabilitiesRootId = findLiabilitiesRootCategoryId(categoriesById);
  if (!liabilitiesRootId) {
    return false;
  }
  return isCategoryDescendantOf(category, liabilitiesRootId, categoriesById);
}
